import React, { useState } from 'react';
import { usePerspectives } from '../../hooks/usePerspectives';
import { getIncidentApiToken } from '../../api/tokenService';
import { normalizeApiUrl } from '../../api/apiClient';
import {
  buildPerspectivesPayload,
  exportPerspectivesPdf,
  emailPerspectives,
} from '../../api/perspectivesApi';
import PerspectiveCard from './PerspectiveCard';
import { aggregateRecommendations, getRoleMeta } from './perspectivesModel';

const PERSPECTIVE_TABS = [
  { id: 'recommendations', label: 'Recommendations', icon: '💡' },
  { id: 'judge', label: 'Judge', icon: '👁️' },
  { id: 'prosecutor', label: 'Prosecutor', icon: '⚖️' },
  { id: 'defense', label: 'Defense', icon: '🛡️' },
];

const PerspectivesPanel = ({ caseData, apiUrl }) => {
  const { perspectives, jurisdiction, metadata, loading, error, generate } = usePerspectives(
    apiUrl
  );
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [activeTab, setActiveTab] = useState('recommendations');

  const recommendations = aggregateRecommendations(perspectives);

  const handleGenerate = async () => {
    try {
      await generate(caseData);
    } catch {
      // error already surfaced through hook state
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const idToken = await getIncidentApiToken();
      const payload = buildPerspectivesPayload(caseData, perspectives, jurisdiction, metadata);
      const response = await exportPerspectivesPdf(payload, apiUrl, idToken);

      if (response.success) {
        const downloadResponse = await fetch(
          normalizeApiUrl(`${apiUrl}${response.data.downloadUrl}`),
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        if (!downloadResponse.ok) throw new Error('PDF download failed');
        const blobUrl = URL.createObjectURL(await downloadResponse.blob());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = response.data.fileName || 'legal-perspectives.pdf';
        link.click();
        URL.revokeObjectURL(blobUrl);
      } else {
        alert('Export failed: ' + (response.data?.error || 'unknown error'));
      }
    } catch (err) {
      console.error('Perspectives export error:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const submitEmail = async () => {
    if (!emailAddresses.trim()) {
      alert('Please enter at least one email address');
      return;
    }
    setSending(true);
    setShowEmailModal(false);
    try {
      const idToken = await getIncidentApiToken();
      const payload = buildPerspectivesPayload(caseData, perspectives, jurisdiction, metadata);
      const response = await emailPerspectives(
        emailAddresses.split(',').map((e) => e.trim()),
        payload,
        apiUrl,
        idToken
      );

      if (response.success) {
        alert('Email sent successfully!');
      } else {
        alert('Email sending failed: ' + (response.data?.error || 'unknown error'));
      }
    } catch (err) {
      console.error('Perspectives email error:', err);
      alert('Email sending failed. Please try again.');
    } finally {
      setSending(false);
      setEmailAddresses('');
    }
  };

  const EmailModal = () => (
    <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Send Perspectives via Email</h3>
          <button className="modal-close" onClick={() => setShowEmailModal(false)}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <label>Email addresses</label>
          <input
            type="text"
            value={emailAddresses}
            onChange={(e) => setEmailAddresses(e.target.value)}
            placeholder="e.g. user@example.com, colleague@company.com"
            autoFocus
          />
          <p className="input-hint">Separate multiple addresses with commas</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowEmailModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submitEmail} disabled={sending}>
            {sending ? 'Sending...' : 'Send Report'}
          </button>
        </div>
      </div>
    </div>
  );

  if (!caseData) {
    return (
      <div className="perspectives-panel">
        <div className="perspectives-placeholder">
          <p>👁️ Submit a case first, then open the Perspectives tab to compare viewpoints.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="perspectives-panel">
      <div className="perspectives-header">
        <div>
          <h3>👁️ Jurisdiction-Aware Perspectives</h3>
          {jurisdiction && <span className="perspectives-jurisdiction">📍 {jurisdiction}</span>}
        </div>
        <div className="perspectives-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? 'Generating…'
              : perspectives
                ? '🔄 Regenerate'
                : '✨ Generate Perspectives'}
          </button>
          <button
            type="button"
            className="btn btn-export"
            onClick={handleExportPDF}
            disabled={exporting || !perspectives}
          >
            {exporting ? 'Exporting…' : '📄 Export PDF'}
          </button>
          <button
            type="button"
            className="btn btn-email"
            onClick={() => setShowEmailModal(true)}
            disabled={sending || !perspectives}
          >
            {sending ? 'Sending…' : '✉️ Send Email'}
          </button>
        </div>
      </div>

      <p className="perspectives-disclaimer">
        These are neutral, advisory viewpoints — not legal advice, verdicts, or predictions about
        case outcome.
      </p>

      {error && <div className="error-message">❌ {error}</div>}

      {loading && (
        <div className="perspectives-loading">
          <div className="spinner"></div>
          <p>Generating defense, prosecutor, and judge viewpoints…</p>
        </div>
      )}

      {!loading && !perspectives && !error && (
        <div className="perspectives-placeholder">
          <p>
            Compare how a defense attorney, a prosecutor, and a judge would each observe this case
            under the selected jurisdiction.
          </p>
        </div>
      )}

      {!loading && perspectives && (
        <>
          <div className="perspectives-tabs" role="tablist" aria-label="Perspective views">
            {PERSPECTIVE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`perspectives-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="perspectives-tab-icon" aria-hidden="true">
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'recommendations' ? (
            <div className="perspectives-recommendations">
              <h4>💡 Neutral Recommendations</h4>
              {recommendations.length > 0 ? (
                <ul>
                  {recommendations.map((rec, idx) => (
                    <li key={idx}>
                      <span className="rec-role">{getRoleMeta(rec.role).title}</span>
                      {rec.text}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="perspective-empty">No recommendations yet.</p>
              )}
            </div>
          ) : (
            <div className="perspectives-grid perspectives-grid-single">
              <PerspectiveCard role={activeTab} perspective={perspectives[activeTab]} />
            </div>
          )}
        </>
      )}

      {!loading && metadata && (
        <p className="perspectives-meta">
          Generated by {metadata.model || metadata.provider || 'AI'}
          {metadata.generatedAt ? ` · ${new Date(metadata.generatedAt).toLocaleString()}` : ''}
        </p>
      )}

      {showEmailModal && <EmailModal />}
    </div>
  );
};

export default PerspectivesPanel;
